import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useMemo, useState } from "react";
import { ModalWrapper } from ".";
import { PrimaryButton } from "../Buttons";
import { TextInput, ToggleInput } from "../Inputs";
import { useApi, useAlert } from "../../hooks";
import { classNames, formatCurrency } from "../../utils/helpers";

export default function CancelConfirmationModal({
  modalOpen,
  title,
  message,
  textToMatch,
  confirmButtonText,
  setModalOpen,
  onConfirm,
  downgradeToFree,
  setDowngradeToFree,
  // SPR-005: when an ownedProductId is supplied, the modal first previews any eligible
  // save-at-cancel Retention offer. Accept keeps the subscription; Decline falls through
  // to the existing cancel-confirm UX. When omitted, the modal behaves exactly as before.
  ownedProductId,
  onOfferAccepted
}) {
  const { fetch, post } = useApi();
  const alert = useAlert();
  const [userInput, setUserInput] = useState("");
  // SPR-011 T-004: the modal now surfaces ALL eligible retention offers (server-ordered best-value first) and
  // lets the user pick; the first (best-value) is pre-selected. res[0] is no longer the only option.
  const [offers, setOffers] = useState([]);
  const [selectedTrialOfferId, setSelectedTrialOfferId] = useState(null);
  const [declined, setDeclined] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const userInputMatches = useMemo(
    () => textToMatch === userInput,
    [textToMatch, userInput]
  );

  // On open: reset local state and, when enabled, preview eligible retention offers.
  // A preview failure or empty list must never block cancellation — we just fall
  // through to today's cancel-confirm UX.
  useEffect(() => {
    if (!modalOpen) return;
    setUserInput("");
    setOffers([]);
    setSelectedTrialOfferId(null);
    setDeclined(false);
    if (!ownedProductId) return;
    fetch(`admin/PreviewCancellationOffers/${ownedProductId}`)
      .then((res) => {
        if (Array.isArray(res) && res.length > 0) {
          setOffers(res);
          // Pre-select the best-value default (server returns best-value first).
          setSelectedTrialOfferId(res[0].trialOfferId);
        }
      })
      .catch((err) => {
        // Never block cancellation on a preview failure — but log it so a genuine
        // preview error is distinguishable from an empty (no-eligible-offer) result.
        console.error("Retention offer preview failed", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, ownedProductId]);

  const showOffer = offers.length > 0 && !declined;

  // Human-readable duration unit (e.g. "Months" -> "months", singularized when count === 1).
  const unitLabel = (durationType, count) => {
    if (!durationType) return "";
    let u = durationType.toLowerCase();
    if (count === 1 && u.endsWith("s")) u = u.slice(0, -1);
    return u;
  };

  // Offer copy that reads clearly for recurring vs single-term (SPR-011 T-004):
  //   RecurringPerPeriod -> "20% off for 6 months" / "$1 for 3 months"
  //   SingleTerm         -> "$1 for a 3-month term" / "20% off for a 3-month term"
  const describeOffer = (offer) => {
    if (!offer) return "";
    const discount = offer.discountPercent != null
      ? `${offer.discountPercent}% off`
      : (offer.discountType === "Fixed" ? formatCurrency(offer.price, true) : (offer.discountType || "Discount applies"));
    if (offer.discountMode === "RecurringPerPeriod") {
      const n = offer.recurringPeriods ?? 1;
      return `${discount} for ${n} ${unitLabel(offer.durationType, n)}`;
    }
    return `${discount} for a ${offer.durationCount}-${unitLabel(offer.durationType, 1)} term`;
  };

  function handleAccept() {
    if (!selectedTrialOfferId) return;
    setAccepting(true);
    post(`admin/AcceptRetentionOffer/${ownedProductId}/${selectedTrialOfferId}`)
      .then(() => {
        alert(
          "Subscription kept",
          "Your discount applies at the next renewal.",
          "success"
        );
        setModalOpen(false);
        if (onOfferAccepted) onOfferAccepted();
      })
      .catch((err) => {
        alert(
          "Server error",
          err?.Message || "Could not apply the retention offer",
          "error"
        );
      })
      .finally(() => setAccepting(false));
  }

  return (
    <ModalWrapper open={modalOpen} setOpen={setModalOpen}>
      <div className="w-[85vw] lg:w-[35vw]">
        {showOffer ? (
          <>
            <div className="pb-4 text-center flex items-center flex-col">
              {title}
            </div>
            <div className="pb-4 mx-6 text-center text-sm text-gray-500">
              {offers.length > 1
                ? "Before you cancel, choose an offer to keep your subscription:"
                : "Before you cancel, here is an offer to keep your subscription:"}
            </div>
            <div className="mb-8 mx-2 flex flex-col gap-2">
              {offers.map((offer, idx) => {
                const isSelected = offer.trialOfferId === selectedTrialOfferId;
                return (
                  <label
                    key={offer.trialOfferId}
                    className={classNames(
                      "p-4 rounded-lg bg-white border-2 cursor-pointer flex flex-row gap-3",
                      isSelected ? "border-primaryColor" : "border-gray-200 hover:border-primaryColor/40"
                    )}
                  >
                    <input
                      type="radio"
                      name="retention-offer"
                      className="mt-1"
                      checked={isSelected}
                      onChange={() => setSelectedTrialOfferId(offer.trialOfferId)}
                    />
                    <div className="flex flex-col grow">
                      <div className="flex flex-row items-center gap-2">
                        <p className="text-lg font-semibold">{offer.name}</p>
                        {idx === 0 && (
                          <span className="inline-flex items-center rounded-full bg-primaryColor/10 px-2 py-0.5 text-xs font-semibold text-primaryColor">
                            Best value
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-gray-700">{describeOffer(offer)}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <PrimaryButton
                text={accepting ? "Applying..." : "Accept offer"}
                onClick={handleAccept}
                disabled={accepting || !selectedTrialOfferId}
              />
              <button
                className="text-sm text-gray-500 hover:text-gray-700 underline"
                onClick={() => setDeclined(true)}
                disabled={accepting}
              >
                Decline (cancel anyway)
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="pb-4 text-center flex items-center flex-col">
              {title}
            </div>
            {message && (
              <div className="pb-8 mx-6 text-center text-sm text-gray-500">
                {message}
              </div>
            )}

            <div className="mb-8">
              <ToggleInput
                label={"Downgrade to free tier"}
                enabled={downgradeToFree}
                setEnabled={setDowngradeToFree}
              />
            </div>

            <div className="mb-8">
              <TextInput
                placeholder={textToMatch}
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                iconColor={userInputMatches ? "success" : "notification"}
                Icon={userInputMatches ? CheckIcon : ExclamationTriangleIcon}
              />
            </div>

            <div className="mt-3">
              <PrimaryButton
                text={confirmButtonText}
                onClick={onConfirm}
                disabled={!userInputMatches}
              />
            </div>
          </>
        )}
      </div>
    </ModalWrapper>
  );
}
